import type { JSONContent } from "@tiptap/core";

import type { RichTextDocument } from "@/components/editor/types";

interface LegacyInlineContent {
  type?: string;
  text?: string;
  href?: string;
  styles?: Record<string, unknown>;
  content?: LegacyInlineContent[];
}

interface LegacyBlockNoteBlock {
  type: string;
  props?: Record<string, unknown>;
  content?: LegacyInlineContent[] | string | null;
  children?: LegacyBlockNoteBlock[];
}

const EMPTY_NOTE_DOCUMENT: RichTextDocument = {
  type: "doc",
  content: [{ type: "paragraph" }],
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function getStringValue(value: unknown, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function getLegacyTextMarks(styles: Record<string, unknown> | undefined) {
  if (!styles) return undefined;

  const marks: JSONContent["marks"] = [];
  if (styles.bold) marks.push({ type: "bold" });
  if (styles.italic) marks.push({ type: "italic" });
  if (styles.underline) marks.push({ type: "underline" });
  if (styles.strike) marks.push({ type: "strike" });
  if (styles.code) marks.push({ type: "code" });

  return marks.length > 0 ? marks : undefined;
}

function convertLegacyInlineContent(content: LegacyBlockNoteBlock["content"]): JSONContent[] {
  if (typeof content === "string") {
    return content.length > 0 ? [{ type: "text", text: content }] : [];
  }

  if (!Array.isArray(content)) {
    return [];
  }

  return content.flatMap((item) => {
    if (!isRecord(item)) return [];

    if (item.type === "link" && Array.isArray(item.content)) {
      return convertLegacyInlineContent(item.content);
    }

    const text = getStringValue(item.text);
    if (!text) return [];

    return [
      {
        type: "text",
        text,
        marks: getLegacyTextMarks(
          isRecord(item.styles) ? (item.styles as Record<string, unknown>) : undefined,
        ),
      },
    ];
  });
}

function convertLegacyListItem(block: LegacyBlockNoteBlock): JSONContent {
  const paragraphContent = convertLegacyInlineContent(block.content);
  const childNodes = Array.isArray(block.children)
    ? convertLegacyBlocksToNodes(block.children)
    : [];

  return {
    type: "listItem",
    content: [
      {
        type: "paragraph",
        content: paragraphContent.length > 0 ? paragraphContent : undefined,
      },
      ...childNodes,
    ],
  };
}

function convertLegacyBlock(block: LegacyBlockNoteBlock): JSONContent[] {
  const props = isRecord(block.props) ? block.props : {};
  const inlineContent = convertLegacyInlineContent(block.content);
  const childNodes = Array.isArray(block.children)
    ? convertLegacyBlocksToNodes(block.children)
    : [];

  switch (block.type) {
    case "paragraph":
      return [
        {
          type: "paragraph",
          content: inlineContent.length > 0 ? inlineContent : undefined,
        },
        ...childNodes,
      ];
    case "heading": {
      const level = Number(props.level);
      return [
        {
          type: "heading",
          attrs: {
            level:
              level === 1 || level === 2 || level === 3
                ? level
                : 2,
          },
          content: inlineContent.length > 0 ? inlineContent : undefined,
        },
        ...childNodes,
      ];
    }
    case "quote":
      return [
        {
          type: "blockquote",
          content: [
            {
              type: "paragraph",
              content: inlineContent.length > 0 ? inlineContent : undefined,
            },
            ...childNodes,
          ],
        },
      ];
    case "codeBlock": {
      const codeText = typeof block.content === "string"
        ? block.content
        : inlineContent
            .map((node) => getStringValue(node.text))
            .join("");

      return [
        {
          type: "codeBlock",
          content: codeText ? [{ type: "text", text: codeText }] : undefined,
        },
      ];
    }
    case "divider":
    case "horizontalRule":
      return [{ type: "horizontalRule" }, ...childNodes];
    case "subNote":
      return [
        {
          type: "subNote",
          attrs: {
            noteId: getStringValue(props.noteId),
            noteTitle: getStringValue(props.noteTitle, "Untitled"),
          },
        },
        ...childNodes,
      ];
    default:
      return [
        {
          type: "paragraph",
          content: inlineContent.length > 0 ? inlineContent : undefined,
        },
        ...childNodes,
      ];
  }
}

function convertLegacyBlocksToNodes(blocks: LegacyBlockNoteBlock[]): JSONContent[] {
  const nodes: JSONContent[] = [];

  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index]!;

    if (
      block.type === "bulletListItem" ||
      block.type === "numberedListItem" ||
      block.type === "checkListItem"
    ) {
      const listType =
        block.type === "numberedListItem" ? "orderedList" : "bulletList";
      const listItems: JSONContent[] = [];

      while (index < blocks.length && blocks[index]?.type === block.type) {
        listItems.push(convertLegacyListItem(blocks[index]!));
        index += 1;
      }

      index -= 1;
      nodes.push({ type: listType, content: listItems });
      continue;
    }

    nodes.push(...convertLegacyBlock(block));
  }

  return nodes;
}

export function createEmptyNoteDocument(): RichTextDocument {
  return {
    type: "doc",
    content: [{ type: "paragraph" }],
  };
}

export function isTipTapDocument(value: unknown): value is RichTextDocument {
  return isRecord(value) && value.type === "doc";
}

export function normalizeNoteContent(content: unknown): RichTextDocument {
  if (isTipTapDocument(content)) {
    if (Array.isArray(content.content) && content.content.length > 0) {
      return content;
    }
    return createEmptyNoteDocument();
  }

  if (Array.isArray(content)) {
    const nodes = convertLegacyBlocksToNodes(
      content.filter(
        (item): item is LegacyBlockNoteBlock =>
          isRecord(item) && typeof item.type === "string",
      ),
    );

    return {
      type: "doc",
      content: nodes.length > 0 ? nodes : EMPTY_NOTE_DOCUMENT.content,
    };
  }

  return createEmptyNoteDocument();
}

const BLOCK_TEXT_BREAK_TYPES = new Set([
  "paragraph",
  "heading",
  "blockquote",
  "codeBlock",
  "bulletList",
  "orderedList",
  "listItem",
  "horizontalRule",
  "subNote",
]);

function collectPlainText(node: JSONContent, fragments: string[]) {
  if (typeof node.text === "string" && node.text.length > 0) {
    fragments.push(node.text);
  }

  if (node.type === "hardBreak" || node.type === "horizontalRule") {
    fragments.push("\n");
  }

  if (node.type === "subNote") {
    const noteTitle =
      isRecord(node.attrs) && typeof node.attrs.noteTitle === "string" ? node.attrs.noteTitle : "";
    if (noteTitle) {
      fragments.push(noteTitle);
    }
  }

  if (!Array.isArray(node.content)) {
    return;
  }

  for (const child of node.content) {
    collectPlainText(child, fragments);
    if (child.type && BLOCK_TEXT_BREAK_TYPES.has(child.type)) {
      fragments.push("\n");
    }
  }
}

export function extractPlainTextFromNoteContent(content: unknown): string {
  const document = normalizeNoteContent(content);
  const fragments: string[] = [];
  collectPlainText(document, fragments);
  return fragments.join("").replace(/\s+/g, " ").trim();
}
